import { useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { CircleCheck, FileDown, FileSignature, KeyRound, Plus, TriangleAlert } from 'lucide-react';
import { getErrorMessage, hasProblemType, keys, portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { ATTORNEY_TYPE_LABELS, formatDate, formatDateTime, hasPermission, proxyAuthorizationSchema, proxyCodeSchema } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  applyProblemToForm,
  Button,
  EmptyState,
  ErrorState,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRootError,
  InputField,
  Select,
  Skeleton,
  StatusBadge,
  TextareaField,
  ToggleGroup,
  toast,
  cn,
} from '@blimo/ui';
import { SignaturePad } from '../components/signature-pad.jsx';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ItemIcon, ItemList, Section } from '../components/ui/section.jsx';
import { ForbiddenPage } from './pages.jsx';

/** Stimmrechtsvollmachten sind eine Eigentümer-Funktion (`ownerships.view`); die API prüft erneut. */
function useCanManageProxies() {
  const { data: me } = useMe();
  return hasPermission(me, 'ownerships.view');
}

const BACK = { to: '/vollmachten', search: { all: undefined }, label: 'Vollmachten' };

/** "Herrn/Frau Erika Muster" / "Verwalter" */
const attorneyText = (proxy) => [proxy.attorney_type_label ?? ATTORNEY_TYPE_LABELS[proxy.attorney_type], proxy.attorney_name].filter(Boolean).join(' ');

function PdfLink({ proxy }) {
  const api = useApi();
  return (
    <Button asChild variant="outline" size="sm" className="w-fit">
      <a href={api.portal.proxyAuthorizations.pdfUrl(proxy.id)} target="_blank" rel="noopener noreferrer">
        <FileDown aria-hidden="true" /> Vollmacht als PDF
        <span className="sr-only"> (öffnet in neuem Tab)</span>
      </a>
    </Button>
  );
}

/* ------------------------------------------------------------------ Liste */

export function ProxiesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const canView = useCanManageProxies();
  const search = useSearch({ strict: false });
  const all = search.all === 1;
  const query = useQuery({ ...portalQueries.proxyAuthorizations(api, all ? { all: true } : {}), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Vollmachten"
        description="Stimmrechtsvollmachten für Eigentümerversammlungen – mit Unterschrift und Bestätigungscode per E-Mail."
        actions={
          <Button asChild>
            <Link to="/vollmachten/neu">
              <Plus aria-hidden="true" /> Neue Vollmacht
            </Link>
          </Button>
        }
      />
      <ToggleGroup
        label="Zeitraum"
        size="default"
        value={all ? 'all' : 'upcoming'}
        onValueChange={(v) => navigate({ to: '/vollmachten', search: { all: v === 'all' ? 1 : undefined }, replace: true })}
        options={[
          { value: 'upcoming', label: 'Anstehende' },
          { value: 'all', label: 'Alle anzeigen' },
        ]}
      />
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : query.data.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          title={all ? 'Noch keine Vollmachten' : 'Keine anstehenden Vollmachten'}
          description={
            all
              ? 'Sie haben noch keine Stimmrechtsvollmacht erteilt.'
              : 'Für kommende Versammlungen liegt keine Vollmacht vor. Ältere Vollmachten sehen Sie unter „Alle anzeigen“.'
          }
          action={
            <Button asChild>
              <Link to="/vollmachten/neu">Vollmacht erteilen</Link>
            </Button>
          }
        />
      ) : (
        <ItemList aria-label="Vollmachten" className={cn(query.isPlaceholderData && 'opacity-60')}>
          {query.data.map((proxy) => (
            <li key={proxy.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <ItemIcon icon={FileSignature} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium">Versammlung am {formatDate(proxy.meeting_date)}</span>
                    <StatusBadge domain="proxyAuthorization" status={proxy.is_confirmed ? 'confirmed' : 'pending'} />
                  </h2>
                  <p className="text-[0.8125rem] text-muted-foreground">
                    {[proxy.weg_name || proxy.property?.name, proxy.weg_location].filter(Boolean).join(', ')}
                  </p>
                  <p className="text-[0.8125rem]">
                    <span className="text-muted-foreground">Bevollmächtigt: </span>
                    {attorneyText(proxy)}
                  </p>
                </div>
              </div>
              <div className="pl-11 sm:pl-0">
                {proxy.is_confirmed ? (
                  <PdfLink proxy={proxy} />
                ) : (
                  <Button asChild size="sm">
                    <Link to="/vollmachten/$proxyId" params={{ proxyId: String(proxy.id) }}>
                      <KeyRound aria-hidden="true" /> Code eingeben
                    </Link>
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ItemList>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Neue Vollmacht */

const ATTORNEY_TYPES = /** @type {const} */ (['name', 'administrator', 'co_owner']);
const FORM_FIELDS = [
  'property_id',
  'principal_name',
  'principal_address',
  'attorney_type',
  'attorney_name',
  'weg_name',
  'weg_location',
  'meeting_date',
  'instructions',
];

export function ProxyCreatePage() {
  const canView = useCanManageProxies();
  if (!canView) return <ForbiddenPage />;
  return <ProxyCreate />;
}

function ProxyCreate() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const properties = useQuery(portalQueries.properties(api));
  // Only properties with a currently owned unit (API decides `is_owned`; it checks again on POST).
  const owned = (properties.data ?? []).filter((p) => p.my_units?.some((u) => u.is_owned));

  if (properties.error) return <ErrorState error={properties.error} onRetry={() => properties.refetch()} />;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={BACK}
        title="Neue Vollmacht"
        description="Nach dem Absenden erhalten Sie einen Bestätigungscode per E-Mail. Erst mit dem Code wird die Vollmacht gültig."
      />
      {properties.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : owned.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          title="Keine eigene Einheit"
          description="Vollmachten können nur für Liegenschaften erteilt werden, in denen Sie aktuell Eigentümer sind."
        />
      ) : (
        <ProxyForm
          properties={owned}
          defaultName={me?.name ?? ''}
          onCreated={(body) => {
            const proxy = body?.data ?? body;
            queryClient.invalidateQueries({ queryKey: keys.portal.resource('proxy-authorizations') });
            if (proxy?.id) queryClient.setQueryData(keys.portal.detail('proxy-authorizations', proxy.id), proxy);
            const to = body?.meta?.code_sent_to;
            toast.success('Vollmacht gespeichert', {
              description: to ? `Der Bestätigungscode wurde an ${to} gesendet.` : 'Der Bestätigungscode wurde per E-Mail gesendet.',
            });
            if (proxy?.id) navigate({ to: '/vollmachten/$proxyId', params: { proxyId: String(proxy.id) } });
            else navigate({ to: '/vollmachten', search: { all: undefined } });
          }}
        />
      )}
    </div>
  );
}

function ProxyForm({ properties, defaultName, onCreated }) {
  const api = useApi();
  const signatureHintId = useId();
  const single = properties.length === 1 ? properties[0] : null;
  const [signature, setSignature] = useState(/** @type {string|null} */ (null));
  const [signatureError, setSignatureError] = useState(/** @type {string|null} */ (null));
  const form = useForm({
    resolver: zodResolver(proxyAuthorizationSchema),
    defaultValues: {
      property_id: single ? String(single.id) : '',
      principal_name: defaultName,
      principal_address: '',
      attorney_type: 'name',
      attorney_name: '',
      weg_name: single?.name ?? '',
      weg_location: single?.address?.city ?? '',
      meeting_date: '',
      instructions: '',
    },
  });
  const attorneyType = useWatch({ control: form.control, name: 'attorney_type' });

  /** WEG name/location follow the chosen property (prefill, still editable). */
  function onPropertyChange(value) {
    const property = properties.find((p) => String(p.id) === value);
    form.setValue('weg_name', property?.name ?? '');
    form.setValue('weg_location', property?.address?.city ?? '');
  }

  const save = form.handleSubmit(async (values) => {
    if (!signature) return;
    try {
      const body = await api.portal.proxyAuthorizations.create({
        property_id: Number(values.property_id),
        principal_name: values.principal_name.trim(),
        principal_address: values.principal_address.trim(),
        attorney_type: values.attorney_type,
        attorney_name: values.attorney_type === 'administrator' ? null : values.attorney_name.trim(),
        weg_name: values.weg_name.trim() || null,
        weg_location: values.weg_location.trim() || null,
        meeting_date: values.meeting_date,
        instructions: values.instructions.trim() || null,
        signature,
      });
      onCreated(body);
    } catch (error) {
      if (error?.status === 404) {
        form.setError('root.server', { type: 'server', message: 'Für diese Liegenschaft sind Sie nicht als Eigentümer eingetragen.' });
        return;
      }
      if (hasProblemType(error, 'invalid-signature')) {
        setSignatureError(error.detail || 'Die Unterschrift ist ungültig. Bitte erneut unterschreiben.');
        return;
      }
      const signatureMessages = error?.status === 422 ? error.errors?.signature : null;
      if (signatureMessages) setSignatureError(Array.isArray(signatureMessages) ? signatureMessages[0] : String(signatureMessages));
      applyProblemToForm(error, form.setError, { fields: FORM_FIELDS });
    }
  });

  const onSubmit = (event) => {
    if (!signature) setSignatureError('Bitte unterschreiben Sie.');
    return save(event);
  };

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex max-w-2xl flex-col gap-4">
        <FormRootError />
        <Section title="Versammlung" bodyClassName="flex flex-col gap-4">
          <FormField
            name="property_id"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Liegenschaft</FormLabel>
                <FormControl>
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      field.onChange(e);
                      onPropertyChange(e.target.value);
                    }}
                  >
                    <option value="">Bitte wählen …</option>
                    {properties.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name || `Liegenschaft #${p.id}`}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <InputField name="weg_name" label="Name der WEG" autoComplete="off" />
            <InputField name="weg_location" label="Ort der WEG" autoComplete="off" />
          </div>
          <InputField name="meeting_date" label="Datum der Versammlung" type="date" />
        </Section>
        <fieldset className="flex flex-col overflow-hidden rounded-xl border bg-card">
          <legend className="sr-only">Vollmachtgeber (Sie)</legend>
          <div aria-hidden="true" className="border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold">
            Vollmachtgeber (Sie)
          </div>
          <div className="flex flex-col gap-4 p-4">
            <InputField name="principal_name" label="Name" autoComplete="name" />
            <InputField name="principal_address" label="Anschrift" autoComplete="street-address" />
          </div>
        </fieldset>
        <div className="flex flex-col overflow-hidden rounded-xl border bg-card">
          <FormField
            name="attorney_type"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <fieldset>
                  <legend className="sr-only">Bevollmächtigt wird</legend>
                  <div aria-hidden="true" className="border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold">
                    Bevollmächtigt wird
                  </div>
                  <div className="grid gap-2 p-4 sm:grid-cols-3">
                    {ATTORNEY_TYPES.map((type) => (
                      <label
                        key={type}
                        className="flex min-h-10 cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-muted/50 has-checked:border-primary has-checked:bg-(--primary-tint)"
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value={type}
                          checked={field.value === type}
                          onChange={() => field.onChange(type)}
                          className="size-4 shrink-0 accent-primary"
                        />
                        <span className="flex-1">{ATTORNEY_TYPE_LABELS[type]}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <FormMessage className="px-4" />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4 px-4 pb-4">
            {attorneyType !== 'administrator' && (
              <InputField
                name="attorney_name"
                label={attorneyType === 'co_owner' ? 'Name des Miteigentümers' : 'Name der bevollmächtigten Person'}
                autoComplete="off"
              />
            )}
            <TextareaField
              name="instructions"
              label="Weisungen (optional)"
              rows={4}
              description="z. B. Abstimmungsverhalten zu einzelnen Tagesordnungspunkten."
            />
          </div>
        </div>
        <div className="flex flex-col overflow-hidden rounded-xl border bg-card">
          <span className="border-b bg-muted/40 px-4 py-2.5 text-sm font-semibold" id={`${signatureHintId}-label`}>
            Unterschrift
          </span>
          <div className="flex flex-col gap-1.5 p-4">
            <SignaturePad
              invalid={Boolean(signatureError)}
              describedBy={signatureError ? signatureHintId : undefined}
              onChange={(uri) => {
                setSignature(uri);
                if (uri) setSignatureError(null);
              }}
            />
            {signatureError && (
              <p id={signatureHintId} role="alert" className="text-sm text-destructive">
                {signatureError}
              </p>
            )}
          </div>
        </div>
        <Button type="submit" className="w-full sm:w-fit" loading={form.formState.isSubmitting}>
          Vollmacht erteilen
        </Button>
      </form>
    </Form>
  );
}

/* ------------------------------------------------------------------ Detail / Code-Bestätigung */

export function ProxyPage() {
  const api = useApi();
  const canView = useCanManageProxies();
  const { proxyId } = useParams({ strict: false });
  const query = useQuery({ ...portalQueries.proxyAuthorization(api, proxyId), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  if (query.error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Vollmacht" back={BACK} />
        <ErrorState error={query.error} onRetry={() => query.refetch()} notFoundTitle="Vollmacht nicht gefunden" />
      </div>
    );
  }
  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
        <PageHeader title="Vollmacht" back={BACK} />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  const proxy = query.data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={BACK}
        compactTitle="Vollmacht"
        title={`Vollmacht für die Versammlung am ${formatDate(proxy.meeting_date)}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {[proxy.weg_name || proxy.property?.name, proxy.weg_location].filter(Boolean).join(', ')}
            <StatusBadge domain="proxyAuthorization" status={proxy.is_confirmed ? 'confirmed' : 'pending'} />
          </span>
        }
      />
      <div className="flex max-w-2xl flex-col gap-1.5 rounded-xl border bg-card px-4 py-3.5 text-[0.8125rem]">
        <p>
          <span className="text-muted-foreground">Vollmachtgeber: </span>
          {[proxy.principal_name, proxy.principal_address].filter(Boolean).join(', ')}
        </p>
        <p>
          <span className="text-muted-foreground">Bevollmächtigt: </span>
          {attorneyText(proxy)}
        </p>
        {proxy.instructions && (
          <p className="whitespace-pre-wrap">
            <span className="text-muted-foreground">Weisungen: </span>
            {proxy.instructions}
          </p>
        )}
      </div>
      {proxy.is_confirmed ? (
        <div className="flex max-w-2xl flex-col items-center gap-3 rounded-xl border bg-card px-4 py-6 text-center">
          <span className="flex size-10 items-center justify-center rounded-full bg-success-subtle text-success-subtle-foreground">
            <CircleCheck aria-hidden="true" className="size-5" />
          </span>
          <p className="text-sm font-semibold text-success-subtle-foreground">
            Vollmacht bestätigt{proxy.confirmed_at ? ` am ${formatDateTime(proxy.confirmed_at)}` : ''}
          </p>
          <PdfLink proxy={proxy} />
        </div>
      ) : (
        <CodeStep proxy={proxy} onRefetch={() => query.refetch()} />
      )}
    </div>
  );
}

function CodeStep({ proxy, onRefetch }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [locked, setLocked] = useState(/** @type {string|null} */ (null));
  const [resendError, setResendError] = useState(/** @type {string|null} */ (null));
  const [resending, setResending] = useState(false);
  const form = useForm({ resolver: zodResolver(proxyCodeSchema), defaultValues: { code: '' } });

  const onSubmit = form.handleSubmit(async ({ code }) => {
    try {
      const updated = await api.portal.proxyAuthorizations.confirm(proxy.id, code.trim());
      queryClient.setQueryData(keys.portal.detail('proxy-authorizations', proxy.id), updated);
      queryClient.invalidateQueries({ queryKey: [...keys.portal.resource('proxy-authorizations'), 'list'] });
      toast.success('Vollmacht bestätigt');
    } catch (error) {
      if (hasProblemType(error, 'invalid-code')) {
        form.setError('code', { type: 'server', message: error.detail || 'Der Bestätigungscode ist nicht korrekt.' }, { shouldFocus: true });
        onRefetch(); // attempts_left
        return;
      }
      if (hasProblemType(error, 'too-many-attempts') || error?.status === 429) {
        setLocked(error.detail || 'Zu viele Fehlversuche. Bitte fordern Sie einen neuen Code an.');
        return;
      }
      applyProblemToForm(error, form.setError, { fields: ['code'] });
    }
  });

  async function resend() {
    setResending(true);
    setResendError(null);
    try {
      const result = await api.portal.proxyAuthorizations.resendCode(proxy.id);
      toast.success(result?.code_sent_to ? `Neuer Code an ${result.code_sent_to} gesendet` : 'Neuer Code gesendet');
      setLocked(null);
      form.reset({ code: '' });
      onRefetch();
    } catch (error) {
      setResendError(getErrorMessage(error, 'Der Code konnte nicht gesendet werden.'));
    } finally {
      setResending(false);
    }
  }

  return (
    <section aria-labelledby="code-heading" className="flex max-w-2xl flex-col gap-4 rounded-xl border bg-card p-4 md:p-5">
      <div className="flex flex-col gap-1">
        <h2 id="code-heading" className="text-base font-semibold">
          Bestätigungscode eingeben
        </h2>
        <p className="text-[0.8125rem] text-muted-foreground">
          Wir haben Ihnen einen Code per E-Mail gesendet{proxy.code_sent_at ? ` (am ${formatDateTime(proxy.code_sent_at)})` : ''}. Mit dem Code wird die
          Vollmacht verbindlich.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {locked ? (
          <Alert variant="destructive" role="alert">
            <TriangleAlert aria-hidden="true" />
            <div className="flex flex-col gap-2">
              <AlertTitle>Code gesperrt</AlertTitle>
              <AlertDescription>{locked}</AlertDescription>
              <Button type="button" className="w-fit" onClick={resend} loading={resending}>
                Neuen Code anfordern
              </Button>
            </div>
          </Alert>
        ) : (
          <Form {...form}>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
              <FormRootError />
              <InputField
                name="code"
                label="Bestätigungscode"
                autoComplete="one-time-code"
                inputMode="numeric"
                description={typeof proxy.attempts_left === 'number' ? `Verbleibende Versuche: ${proxy.attempts_left}` : undefined}
              />
              <div className="flex flex-wrap gap-2">
                <Button type="submit" className="flex-1 sm:flex-none" loading={form.formState.isSubmitting}>
                  Bestätigen
                </Button>
                <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={resend} loading={resending}>
                  Neuen Code anfordern
                </Button>
              </div>
            </form>
          </Form>
        )}
        {resendError && (
          <Alert variant="destructive">
            <AlertDescription>{resendError}</AlertDescription>
          </Alert>
        )}
      </div>
    </section>
  );
}
